import React from "react";
import { ArrowRight, Mail } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";

const EMAIL = "ida@brandnewmoneyfilms.com";
const MAILTO = (subject) =>
  `mailto:${EMAIL}${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`;

const CRAFTS = [
  "Directors",
  "Creative Directors",
  "Writers",
  "Strategists",
  "Cinematographers",
  "Camera Teams",
  "Lighting Teams",
  "Production Managers",
  "Producers",
  "Editors",
  "Colorists",
  "VFX Artists",
  "Motion Designers",
  "3D Artists",
  "AI Artists",
  "Prompt Engineers",
  "Music Producers",
  "Sound Designers",
  "Production Designers",
  "Art Directors",
  "Stylists",
  "Hair Artists",
  "Makeup Artists",
  "Models",
  "Actors",
  "Dancers",
  "Photographers",
  "Influencers",
  "Creators",
  "Event Producers",
  "Event Teams",
  "Brand Consultants",
  "Studios",
  "Equipment Rental Partners",
  "Production Houses",
  "Agencies",
  "Location Owners",
  "Hotels",
  "Restaurants",
  "Travel Partners",
  "Drone Operators",
];

const OPPORTUNITIES = [
  { n: "01", title: "Full Time", body: "Long-term craft, deep ownership." },
  { n: "02", title: "Part Time", body: "Steady collaboration, flexible hours." },
  { n: "03", title: "Freelance", body: "Project by project. On your terms." },
  { n: "04", title: "Contract", body: "Multi-month engagements with a clear scope." },
  { n: "05", title: "Internships", body: "Real productions. Not coffee runs." },
  { n: "06", title: "Fresh Graduates", body: "First break with a serious team." },
  { n: "07", title: "Film Students", body: "Sets, cameras, deadlines. All of it." },
  { n: "08", title: "Design Students", body: "Brand systems, typography, motion." },
  { n: "09", title: "Marketing Students", body: "Live campaigns, real numbers." },
  { n: "10", title: "Remote Collaborators", body: "Anywhere. If the work is exceptional." },
];

const COLLEGES = [
  "Internships",
  "Live Industry Projects",
  "Workshops",
  "Guest Lectures",
  "Film Challenges",
  "Creative Labs",
  "Campus Productions",
  "Portfolio Reviews",
];

/** Tasteful fade-up when the element enters the viewport. */
// Reveal is now imported from '@/components/Reveal'

export default function CreateWithUs() {
  return (
    <div
      className="min-h-screen bg-white text-ink flex flex-col"
      data-testid="create-with-us-page"
    >
      <Nav />

      {/* HERO */}
      <section className="pt-20 md:pt-32 pb-20 md:pb-28">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="eyebrow eyebrow-denim mb-8" data-testid="cwu-eyebrow">
              Create With Us
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h1
              data-testid="cwu-hero-headline"
              className="font-display font-extrabold text-ink leading-[0.95] tracking-[-0.045em] text-[44px] sm:text-[68px] md:text-[92px] lg:text-[108px] max-w-[1000px]"
            >
              Not everyone <span className="font-serif-italic font-light">works</span> with us.
              <br />
              Those who do, <span className="font-serif-italic font-light text-denim-bnm">build</span> with us.
            </h1>
          </Reveal>
          <Reveal delay={180}>
            <p className="text-ink text-[17px] md:text-[19px] leading-[1.7] mt-10 md:mt-12 max-w-[640px]">
              We&rsquo;re looking for passionate people across film, design,
              photography, writing, strategy, music, fashion, technology and
              every form of creativity.
            </p>
          </Reveal>
          <Reveal delay={260}>
            <p className="text-muted-ink text-[17px] md:text-[19px] leading-[1.7] mt-6 max-w-[640px]">
              If you obsess over making exceptional work, we&rsquo;d love to
              hear from you.
            </p>
          </Reveal>
          <Reveal delay={320}>
            <a
              href={MAILTO("Create With Us")}
              className="btn-denim mt-10 md:mt-12 inline-flex"
              data-testid="cwu-hero-cta"
            >
              <span>Create With Us</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </Reveal>
          <Reveal delay={340}>
            <div className="mt-12 md:mt-14 flex flex-wrap gap-3">
              <span className="pill">No corporate culture</span>
              <span className="pill">No hierarchy</span>
              <span className="pill">Just people who love creating</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 01, COLLABORATE */}
      <section
        className="border-t border-bnm bg-[#fafafa]"
        data-testid="cwu-collaborate"
      >
        <div className="max-w-[1180px] mx-auto px-6 md:px-10 py-20 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-14 md:mb-20">
            <Reveal className="md:col-span-4">
              <div className="eyebrow eyebrow-denim mb-5">01, Collaborate</div>
              <h2 className="font-display font-extrabold text-[36px] sm:text-[52px] md:text-[64px] tracking-[-0.04em] leading-[1.02] text-ink">
                If you&rsquo;re exceptionally good at what you do, we&rsquo;d
                love to <span className="font-serif-italic font-light">know</span> you.
              </h2>
            </Reveal>
            <Reveal className="md:col-span-7 md:col-start-6" delay={120}>
              <p className="text-muted-ink text-[17px] md:text-[19px] leading-[1.75]">
                Join our growing network of creative collaborators. We work
                across brand films, campaigns, product launches, events, and
                everything in between, and we work with the best people we can
                find, wherever they are.
              </p>
            </Reveal>
          </div>

          {/* Chip cloud */}
          <Reveal>
            <div className="flex flex-wrap gap-2.5" data-testid="cwu-crafts">
              {CRAFTS.map((c, i) => (
                <span
                  key={c}
                  data-testid={`cwu-craft-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className="px-4 py-2.5 rounded-full border border-bnm bg-white text-[13.5px] md:text-[14px] text-ink transition-all duration-300 hover:-translate-y-0.5 hover:border-black hover:shadow-[0_6px_18px_rgba(10,10,10,0.06)]"
                  style={{ transitionDelay: `${Math.min(i * 12, 300)}ms` }}
                >
                  {c}
                </span>
              ))}
              <span className="px-4 py-2.5 rounded-full border border-black bg-white text-[13.5px] md:text-[14px] text-ink italic">
                &hellip; anyone who creates exceptional work
              </span>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className="mt-14 md:mt-20 bnm-card p-7 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="max-w-[560px]">
                <div className="eyebrow eyebrow-denim mb-3">Introduce yourself</div>
                <div className="font-display font-extrabold text-[24px] md:text-[32px] tracking-[-0.03em] leading-[1.15] text-ink">
                  Think you&rsquo;d be a{" "}
                  <span className="font-serif-italic font-light">great</span>{" "}
                  fit?
                </div>
                <p className="text-muted-ink text-[15px] md:text-[16px] mt-3 leading-[1.65]">
                  Send us your work. Portfolio, Instagram, LinkedIn, showreel,
                  or simply a note about who you are and what you love making.
                </p>
              </div>
              <a
                href={MAILTO("I'd love to collaborate")}
                className="btn-denim shrink-0"
                data-testid="cwu-email-collab"
              >
                <Mail className="w-4 h-4" />
                <span>{EMAIL}</span>
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 02, JOIN THE TEAM */}
      <section className="border-t border-bnm" data-testid="cwu-join-team">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10 py-20 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-14 md:mb-20">
            <Reveal className="md:col-span-5">
              <div className="eyebrow eyebrow-denim mb-5">02, Join the Team</div>
              <h2 className="font-display font-extrabold text-[36px] sm:text-[52px] md:text-[64px] tracking-[-0.04em] leading-[1.02] text-ink">
                Don&rsquo;t send us a{" "}
                <span className="font-serif-italic font-light">résumé</span>{" "}
                because you think you have to.
              </h2>
            </Reveal>
            <Reveal className="md:col-span-6 md:col-start-7" delay={120}>
              <p className="text-ink text-[17px] md:text-[19px] leading-[1.75]">
                Show us work you&rsquo;re proud of. We&rsquo;re building Brand
                New Money into one of India&rsquo;s most exciting creative
                companies, with the ambition to produce for brands anywhere in
                the world.
              </p>
              <p className="text-muted-ink text-[16px] md:text-[17px] leading-[1.8] mt-6">
                We&rsquo;re looking for people who are curious. Kind.
                Reliable. Creative. Obsessed with craft. Open to learning.
                People who take ownership, and make everyone around them better.
              </p>
            </Reveal>
          </div>

          <Reveal>
            <div className="eyebrow eyebrow-muted mb-6">Opportunities</div>
          </Reveal>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
            data-testid="cwu-opportunities"
          >
            {OPPORTUNITIES.map((o, i) => (
              <Reveal key={o.title} delay={i * 40}>
                <a
                  href={MAILTO(`Interested in ${o.title.toLowerCase()}`)}
                  className="bnm-card p-6 md:p-7 block group"
                  data-testid={`cwu-opp-${o.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="eyebrow eyebrow-denim">{o.n}</div>
                    <ArrowRight className="w-4 h-4 text-muted-ink group-hover:text-ink group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="font-display font-extrabold text-[22px] md:text-[24px] tracking-[-0.025em] leading-[1.15] text-ink group-hover:text-denim-bnm transition-colors">
                    {o.title}
                  </h3>
                  <p className="text-muted-ink text-[14.5px] mt-3 leading-[1.6]">
                    {o.body}
                  </p>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STUDENTS */}
      <section
        className="border-t border-bnm bg-[#fafafa]"
        data-testid="cwu-students"
      >
        <div className="max-w-[1180px] mx-auto px-6 md:px-10 py-20 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
            <Reveal className="md:col-span-5">
              <div className="eyebrow eyebrow-denim mb-5">Students</div>
              <h2 className="font-display font-extrabold text-[36px] sm:text-[52px] md:text-[64px] tracking-[-0.04em] leading-[1.02] text-ink">
                Learn by <span className="font-serif-italic font-light">creating</span>.
              </h2>
            </Reveal>
            <Reveal className="md:col-span-6 md:col-start-7" delay={120}>
              <p className="text-ink text-[17px] md:text-[19px] leading-[1.75]">
                We love working with students who want real industry experience.
                Internships here should feel meaningful, not like coffee runs.
              </p>
              <p className="text-muted-ink text-[16px] md:text-[17px] leading-[1.8] mt-6">
                If you&rsquo;re willing to learn, we&rsquo;ll give you the
                chance to work on real productions, real campaigns, and real
                creative projects, alongside a team that cares about your
                growth as much as it cares about the work.
              </p>
              <a
                href={MAILTO("I'm a student interested in Brand New Money")}
                className="btn-ghost mt-10 inline-flex"
                data-testid="cwu-email-students"
              >
                <Mail className="w-4 h-4" />
                <span>Write to us</span>
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* COLLEGES */}
      <section className="border-t border-bnm" data-testid="cwu-colleges">
        <div className="max-w-[1180px] mx-auto px-6 md:px-10 py-20 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-12 md:mb-16">
            <Reveal className="md:col-span-5">
              <div className="eyebrow eyebrow-denim mb-5">Colleges</div>
              <h2 className="font-display font-extrabold text-[36px] sm:text-[52px] md:text-[64px] tracking-[-0.04em] leading-[1.02] text-ink">
                Let&rsquo;s <span className="font-serif-italic font-light">build</span> together.
              </h2>
            </Reveal>
            <Reveal className="md:col-span-6 md:col-start-7" delay={120}>
              <p className="text-ink text-[17px] md:text-[19px] leading-[1.75]">
                We welcome collaborations with colleges and universities, from
                one-off workshops to year-long partnerships. If you&rsquo;re on
                faculty or run a creative program, we&rsquo;d love to hear from
                you.
              </p>
            </Reveal>
          </div>
          <Reveal>
            <div className="flex flex-wrap gap-2.5">
              {COLLEGES.map((c) => (
                <span
                  key={c}
                  data-testid={`cwu-college-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className="px-4 py-2.5 rounded-full border border-bnm bg-white text-[14px] text-ink transition-all duration-300 hover:-translate-y-0.5 hover:border-black hover:shadow-[0_6px_18px_rgba(10,10,10,0.06)]"
                >
                  {c}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={100}>
            <a
              href={MAILTO("College collaboration")}
              className="btn-ghost mt-12 inline-flex"
              data-testid="cwu-email-colleges"
            >
              <Mail className="w-4 h-4" />
              <span>Reach out to us</span>
            </a>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-bnm" data-testid="cwu-final-cta">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-28 md:py-40 text-center">
          <Reveal>
            <div className="eyebrow eyebrow-denim mb-8">One last thing</div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display font-extrabold text-[40px] sm:text-[60px] md:text-[88px] tracking-[-0.045em] leading-[1.0] text-ink">
              Let&rsquo;s make something
              <br />
              worth <span className="font-serif-italic font-light text-denim-bnm">remembering</span>.
            </h2>
          </Reveal>
          <Reveal delay={180}>
            <p className="text-muted-ink text-[17px] md:text-[19px] mt-8 md:mt-10 leading-[1.7] max-w-[620px] mx-auto">
              Whether you&rsquo;re looking to collaborate, join the team, or
              simply introduce yourself, we&rsquo;d love to hear from you. Send
              us your portfolio, your work, or just your story.
            </p>
          </Reveal>
          <Reveal delay={260}>
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <a
                href={MAILTO("My work, for Brand New Money")}
                className="btn-denim w-full sm:w-auto"
                data-testid="cwu-cta-primary"
              >
                <Mail className="w-4 h-4" />
                <span>Email Your Work</span>
              </a>
              <a
                href={MAILTO("Hello, starting a conversation")}
                className="btn-ghost w-full sm:w-auto"
                data-testid="cwu-cta-secondary"
              >
                <span>Start a Conversation</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </Reveal>
          <Reveal delay={340}>
            <div className="mt-10 text-[13px] text-muted-ink eyebrow eyebrow-muted">
              {EMAIL}
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
